/**
 * Synth Console shader helpers — web (three.js) approximations of the game's
 * URP Shader Graphs. All three game shaders (`SH_SynthRiders_Characters`,
 * `SH_Notes`, `SH_WallObstacles`) are UNLIT and lean on the same building
 * blocks — a Fresnel rim, an emissive glow, and a time-based flicker — so the
 * web side shares those primitives instead of porting each graph node-by-node.
 *
 *  - applyRimGlow(): the character shader's signature look (rim/Fresnel + a
 *    small emissive lift) added to an existing lit material via onBeforeCompile,
 *    which keeps skinning and the baked outfit textures intact.
 *  - makeNeonMaterial(): the shared Notes/Obstacles look — an unlit emissive
 *    core with a Fresnel edge and optional flicker (for the note/obstacle meshes
 *    once they replace the placeholder spheres / line frames).
 */

import * as THREE from 'three';

export interface RimGlowOptions {
  /** Rim/Fresnel colour (the game uses a cool cyan-white rim). */
  color?: THREE.ColorRepresentation;
  /** Fresnel falloff exponent — higher = tighter rim. */
  power?: number;
  /** Rim intensity added into the emissive channel. */
  strength?: number;
}

/**
 * Add a `SH_SynthRiders_Characters`-style rim/Fresnel emissive glow to a
 * standard material. Patches the lit shader's emissive term, so the material
 * keeps responding to lights, textures and skinning — we only add the rim.
 */
export function applyRimGlow(
  material: THREE.MeshStandardMaterial,
  { color = '#8fe3ff', power = 2.5, strength = 0.5 }: RimGlowOptions = {},
): void {
  const rimColor = new THREE.Color(color);
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uRimColor = { value: rimColor };
    shader.uniforms.uRimPower = { value: power };
    shader.uniforms.uRimStrength = { value: strength };
    shader.fragmentShader =
      'uniform vec3 uRimColor;\nuniform float uRimPower;\nuniform float uRimStrength;\n' +
      shader.fragmentShader.replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        {
          // vViewPosition points from the fragment to the camera in view space.
          float rim = 1.0 - max(dot(normalize(normal), normalize(vViewPosition)), 0.0);
          rim = pow(clamp(rim, 0.0, 1.0), uRimPower);
          totalEmissiveRadiance += uRimColor * rim * uRimStrength;
        }`,
      );
  };
  material.needsUpdate = true;
}

export interface NeonMaterialOptions {
  /** Core/emissive colour (console left cyan #1CC5F0 / right magenta #F02D87…). */
  color: THREE.ColorRepresentation;
  /** Fresnel edge colour (defaults to a brighter tint of `color`). */
  fresnelColor?: THREE.ColorRepresentation;
  /** Fresnel exponent. */
  fresnelPower?: number;
  /** Base emissive intensity of the core. */
  intensity?: number;
  /** Flicker amount (0 = steady) and speed (Hz-ish), à la SH_Notes `_Flicker`. */
  flicker?: number;
  flickerSpeed?: number;
  /** Render transparent (obstacles) vs opaque (note cores). */
  transparent?: boolean;
  opacity?: number;
}

/**
 * The shared Notes / WallObstacles neon look: an unlit emissive surface with a
 * Fresnel edge and optional time flicker. Returns a ShaderMaterial; advance its
 * `uTime` uniform each frame (`mat.uniforms.uTime.value = clock`) for flicker.
 */
export function makeNeonMaterial(opts: NeonMaterialOptions): THREE.ShaderMaterial {
  const {
    color,
    fresnelColor,
    fresnelPower = 2.0,
    intensity = 1.4,
    flicker = 0.0,
    flickerSpeed = 6.0,
    transparent = false,
    opacity = 1.0,
  } = opts;
  const core = new THREE.Color(color);
  const edge = new THREE.Color(fresnelColor ?? color).lerp(new THREE.Color('#ffffff'), 0.4);

  return new THREE.ShaderMaterial({
    transparent,
    depthWrite: !transparent,
    blending: transparent ? THREE.AdditiveBlending : THREE.NormalBlending,
    uniforms: {
      uCore: { value: core },
      uEdge: { value: edge },
      uFresnelPower: { value: fresnelPower },
      uIntensity: { value: intensity },
      uFlicker: { value: flicker },
      uFlickerSpeed: { value: flickerSpeed },
      uOpacity: { value: opacity },
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormalV;
      varying vec3 vViewV;
      void main() {
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vNormalV = normalMatrix * normal;
        vViewV = -mv.xyz;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uCore; uniform vec3 uEdge;
      uniform float uFresnelPower; uniform float uIntensity;
      uniform float uFlicker; uniform float uFlickerSpeed; uniform float uOpacity; uniform float uTime;
      varying vec3 vNormalV; varying vec3 vViewV;
      void main() {
        float fres = 1.0 - max(dot(normalize(vNormalV), normalize(vViewV)), 0.0);
        fres = pow(clamp(fres, 0.0, 1.0), uFresnelPower);
        float flick = 1.0 - uFlicker * (0.5 + 0.5 * sin(uTime * uFlickerSpeed));
        vec3 col = mix(uCore, uEdge, fres) * uIntensity * flick;
        float a = uOpacity * (0.55 + 0.45 * fres);
        gl_FragColor = vec4(col, a);
      }
    `,
  });
}
